/**
 * Route guard that resolves to one of:
 *
 *  - `<Outlet />`              — user is logged in AND active
 *  - redirect /pending         — logged in but admin hasn't approved yet
 *  - redirect /login           — no token at all
 *  - redirect /                — admin-only route, user is not admin
 *
 * Wrap once in the router; renders children only when guard passes.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useMe } from '@/hooks/useAuthApi'

interface ProtectedRouteProps {
  /** Require role='admin'. Defaults to false (any active user). */
  requireAdmin?: boolean
  /** Allow pending (`is_active=false`) users through. Used for /pending. */
  allowPending?: boolean
}

export function ProtectedRoute({
  requireAdmin = false, allowPending = false,
}: ProtectedRouteProps) {
  const location = useLocation()
  const accessToken = useAuthStore((s) => s.accessToken)
  const user = useAuthStore((s) => s.user)

  // 1. No token → straight to login
  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  // 2. Have token but no user yet — fetch /me; while loading, show nothing
  //    (cheap — happens once on a cold page reload).
  const { isLoading, isError } = useMe({ enabled: !user })
  if (!user) {
    if (isError) return <Navigate to="/login" replace />
    if (isLoading) return null
    return null
  }

  // 3. Pending account
  if (!user.is_active && !allowPending) {
    return <Navigate to="/pending" replace />
  }

  // 4. Admin gate
  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/app" replace />
  }

  return <Outlet />
}
